/**
 * Modern Input component with clean focus states and smooth transitions
 * Follows Apple HIG and Nothing design principles
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  TextInput,
  View,
  Text,
  Pressable,
  Animated,
  TextInputProps,
  AccessibilityInfo,
} from 'react-native';
import { useTheme } from '../../themes';
import { useHaptics } from '../../hooks/useHaptics';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colorTokens } from '../../tokens';
import { BaseInputProps } from './types';

export interface InputProps
  extends Omit<TextInputProps, 'style'>,
    BaseInputProps {
  onFocus?: (e: any) => void;
  onBlur?: (e: any) => void;
  onChangeText?: (text: string) => void;
}

export function Input({
  label,
  error,
  hint,
  size = 'md',
  variant = 'default',
  fullWidth = true,
  disabled = false,
  required = false,
  leftIcon,
  rightIcon,
  onFocus,
  onBlur,
  onChangeText,
  ...textInputProps
}: InputProps) {
  const { theme } = useTheme();
  const haptics = useHaptics();
  const reduceMotion = useReducedMotion();

  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(
    textInputProps.value || textInputProps.defaultValue || ''
  );

  const focusAnimation = useRef(new Animated.Value(0)).current;
  const labelAnimation = useRef(new Animated.Value(value ? 1 : 0)).current;
  const inputRef = useRef<TextInput>(null);

  // Animation configurations
  const animationConfig = {
    duration: reduceMotion ? 0 : 200,
    useNativeDriver: false,
  };

  // Handle focus
  const handleFocus = useCallback(
    (e: any) => {
      setIsFocused(true);
      haptics.impact('light');

      Animated.parallel([
        Animated.timing(focusAnimation, {
          toValue: 1,
          ...animationConfig,
        }),
        Animated.timing(labelAnimation, {
          toValue: 1,
          ...animationConfig,
        }),
      ]).start();

      onFocus?.(e);
    },
    [focusAnimation, labelAnimation, animationConfig, haptics, onFocus]
  );

  // Handle blur
  const handleBlur = useCallback(
    (e: any) => {
      setIsFocused(false);

      Animated.timing(focusAnimation, {
        toValue: 0,
        ...animationConfig,
      }).start();

      if (!value) {
        Animated.timing(labelAnimation, {
          toValue: 0,
          ...animationConfig,
        }).start();
      }

      onBlur?.(e);
    },
    [focusAnimation, labelAnimation, animationConfig, value, onBlur]
  );

  // Handle text change
  const handleChangeText = useCallback(
    (text: string) => {
      setValue(text);

      if (text && !value) {
        Animated.timing(labelAnimation, {
          toValue: 1,
          ...animationConfig,
        }).start();
      } else if (!text && value) {
        Animated.timing(labelAnimation, {
          toValue: 0,
          ...animationConfig,
        }).start();
      }

      onChangeText?.(text);
    },
    [labelAnimation, animationConfig, value, onChangeText]
  );

  // Handle container press (focus input)
  const handleContainerPress = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  // Get input dimensions based on size
  const getInputHeight = () => {
    switch (size) {
      case 'sm':
        return theme.spacing.spacing[8]; // 32px
      case 'lg':
        return theme.spacing.spacing[12]; // 48px
      default:
        return theme.spacing.touchTargets.minimum; // 44px
    }
  };

  // Get font size based on size
  const getFontSize = () => {
    switch (size) {
      case 'sm':
        return theme.typography.fontSize.sm.size;
      case 'lg':
        return theme.typography.fontSize.lg.size;
      default:
        return theme.typography.fontSize.base.size;
    }
  };

  // Get colors based on state
  const getColors = () => {
    if (error) {
      return {
        border: colorTokens.semantic.error.DEFAULT,
        background: colorTokens.semantic.error.light,
        text: theme.colors.text.primary,
        label: colorTokens.semantic.error.DEFAULT,
        placeholder: theme.colors.text.tertiary,
      };
    }

    if (disabled) {
      return {
        border: theme.colors.border.secondary,
        background: theme.colors.surface.secondary,
        text: theme.colors.text.tertiary,
        label: theme.colors.text.tertiary,
        placeholder: theme.colors.text.tertiary,
      };
    }

    return {
      border: isFocused
        ? theme.colors.border.focus
        : theme.colors.border.primary,
      background:
        variant === 'filled'
          ? theme.colors.surface.secondary
          : theme.colors.surface.primary,
      text: theme.colors.text.primary,
      label: isFocused
        ? theme.colors.border.focus
        : theme.colors.text.secondary,
      placeholder: theme.colors.text.tertiary,
    };
  };

  const colors = getColors();
  const inputHeight = getInputHeight();
  const fontSize = getFontSize();

  // Animated border color
  const animatedBorderColor = focusAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [colors.border, theme.colors.border.focus],
  });

  // Animated label position and size
  const animatedLabelTop = labelAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [inputHeight / 2 - 10, -8],
  });

  const animatedLabelScale = labelAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.85],
  });

  const animatedLabelColor = labelAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [colors.placeholder, colors.label],
  });

  return (
    <View style={{ width: fullWidth ? '100%' : undefined }}>
      <Pressable
        onPress={handleContainerPress}
        disabled={disabled}
        accessibilityRole='none'
      >
        <View
          style={{
            position: 'relative',
            marginTop: label ? theme.spacing.spacing[4] : 0,
          }}
        >
          {/* Floating label */}
          {label && (
            <Animated.Text
              style={{
                position: 'absolute',
                left: leftIcon
                  ? theme.spacing.spacing[10]
                  : theme.spacing.spacing[4],
                top: animatedLabelTop,
                fontSize: theme.typography.fontSize.sm.size,
                fontWeight: theme.typography.fontWeight.medium,
                color: animatedLabelColor,
                backgroundColor: colors.background,
                paddingHorizontal: theme.spacing.spacing[1],
                transform: [{ scale: animatedLabelScale }],
                zIndex: 1,
              }}
            >
              {label}
              {required && ' *'}
            </Animated.Text>
          )}

          {/* Input container */}
          <Animated.View
            style={{
              height: inputHeight,
              borderRadius: theme.spacing.borderRadius.lg,
              borderWidth: 2,
              borderColor: error ? colors.border : animatedBorderColor,
              backgroundColor: colors.background,
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: theme.spacing.spacing[4],
              ...theme.spacing.elevation[isFocused ? 2 : 1],
            }}
          >
            {/* Left icon */}
            {leftIcon && (
              <View style={{ marginRight: theme.spacing.spacing[2] }}>
                {leftIcon}
              </View>
            )}

            {/* Text input */}
            <TextInput
              ref={inputRef}
              {...textInputProps}
              style={{
                flex: 1,
                fontSize,
                fontFamily: theme.typography.fontFamily.sans.join(', '),
                color: colors.text,
                paddingVertical: 0,
                textAlignVertical: 'center',
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChangeText={handleChangeText}
              placeholderTextColor={colors.placeholder}
              editable={!disabled}
              accessibilityLabel={label}
              accessibilityHint={hint}
              accessibilityState={{
                disabled,
              }}
            />

            {/* Right icon */}
            {rightIcon && (
              <View style={{ marginLeft: theme.spacing.spacing[2] }}>
                {rightIcon}
              </View>
            )}
          </Animated.View>
        </View>
      </Pressable>

      {/* Error message */}
      {error && (
        <Animated.View
          style={{
            marginTop: theme.spacing.spacing[1],
            opacity: 1,
          }}
        >
          <Text
            style={{
              fontSize: theme.typography.fontSize.sm.size,
              color: colorTokens.semantic.error.DEFAULT,
              fontFamily: theme.typography.fontFamily.sans.join(', '),
            }}
            accessibilityRole='alert'
          >
            {error}
          </Text>
        </Animated.View>
      )}

      {/* Hint message */}
      {hint && !error && (
        <Text
          style={{
            fontSize: theme.typography.fontSize.sm.size,
            color: theme.colors.text.tertiary,
            fontFamily: theme.typography.fontFamily.sans.join(', '),
            marginTop: theme.spacing.spacing[1],
          }}
        >
          {hint}
        </Text>
      )}
    </View>
  );
}
